import classNames from 'classnames';
import Button, { ButtonProps } from '@/components/molecules/button/button';
import SearchBarSection from '@/components/molecules/search-bar-section/search-bar-section';
import {
  AreaData,
  LocationChangeParameters,
  RoomsState,
  SearchBarWrapperSectionData,
  SearchBarWrapperSectionType,
} from '@/components/organisms/search-bar/search-bar.types';
import SearchBarAreaSelectorButton from '@/components/organisms/search-bar/shared/search-bar-area/search-bar-area-selector-button/search-bar-area-selector-button';
import SearchBarAreaSelectorLedge from '@/components/organisms/search-bar/shared/search-bar-area/search-bar-area-selector-ledge/search-bar-area-selector-ledge';
import { useSearchContext } from '@/libraries/contexts/search-context';
import SearchBarDropdown from '../search-bar-dropdown/search-bar-dropdown';
import styles from './search-bar-wrapper.module.scss';

export interface SearchBarWrapperProps {
  /** The active section id */
  activeSection?: number;
  /** The area section data, used to render the area selector button and ledge */
  areaSection?: SearchBarWrapperSectionData;
  /** Label to show in the button  */
  buttonLabel: string;
  /** Indicates whether the search bar is only buttons to open the widget modal  */
  buttonMode?: boolean;
  /** Indicates whether the search button is enabled  */
  canSubmit?: boolean;
  /** The class name to apply */
  className?: string;
  /** Indicates whether to show the compact version */
  compact?: boolean;
  /** Whether to remove range styles */
  customRangeStyle?: boolean;
  /** Indicates whether the search bar is used in the campaign page */
  isInCampaign?: boolean;
  /** Indicates whether the date section is loading */
  isLoadingDate?: boolean;
  /** Indicates whether the locations section is loading */
  isLoadingLocations?: boolean;
  /** An event that is triggered on date change */
  onChangeDate?: (dates: Date[]) => void;
  /** An event that is triggered on duration change */
  onChangeDuration?: (id: string) => void;
  /** An event that is triggered on location change */
  onChangeLocations?: ({
    destinationRegions,
    destinations,
    hotels,
    locations,
  }: LocationChangeParameters) => void;
  /** An event that is triggered on persons change */
  onChangePersons?: (roomsState: RoomsState) => void;
  /** An event that is triggered on transportation change */
  onChangeTransportation?: (id: string) => void;
  /** An event that is triggered on click back */
  onClickBack?: () => void;
  /** An event that is triggered on close click */
  onClickClose?: () => void;
  /** An event that is triggered on next click */
  onClickNext?: () => void;
  /** An event that is triggered on close click */
  onClickSearch?: ButtonProps['onClick'];
  /** An event that is triggered on search bar section click */
  onClickSection?: (id: number) => void;
  /** The search settings */
  searchSettings?: Content.Components['TravelPortalSearchSettingsDto'];
  /** Sections to render in the search bar */
  sections?: SearchBarWrapperSectionData[];
}

const SearchBarWrapper = ({
  activeSection = -1,
  areaSection,
  buttonLabel,
  buttonMode = false,
  canSubmit = false,
  className,
  compact = false,
  customRangeStyle = false,
  isInCampaign = false,
  isLoadingDate = false,
  isLoadingLocations = false,
  onChangeDate,
  onChangeDuration,
  onChangeLocations,
  onChangePersons,
  onChangeTransportation,
  onClickBack,
  onClickClose,
  onClickNext,
  onClickSearch,
  onClickSection,
  searchSettings,
  sections = [],
  ...rest
}: SearchBarWrapperProps) => {
  const { area } = useSearchContext();

  const hasAreaSelectorLedge = !compact && !isInCampaign;
  const hasAreaSelectorSection =
    compact &&
    !isInCampaign &&
    sections?.some(
      section => section.type === SearchBarWrapperSectionType.AREA,
    );

  const renderSection = (section: SearchBarWrapperSectionData) => {
    const isAreaSelectorButtonWrapper =
      section.type === SearchBarWrapperSectionType.AREA;

    const wrapperProps: Record<string, any> = {
      className: classNames(styles.searchSectionWrapper, {
        [styles.isActive]: section.id === activeSection,
        [styles.isAreaSelectorButtonWrapper]: isAreaSelectorButtonWrapper,
      }),
      ...(isAreaSelectorButtonWrapper && { 'data-area': area }),
    };

    return (
      <div key={section.id} {...wrapperProps}>
        {isAreaSelectorButtonWrapper ? (
          <SearchBarAreaSelectorButton
            areas={(areaSection?.dropdownData as AreaData)?.areas ?? []}
            className={styles.searchSection}
            disabled={section.disabled}
            isActive={section.id === activeSection}
            isNotified={section.isNotified}
            onClick={() => onClickSection?.(section.id)}
          />
        ) : (
          <SearchBarSection
            className={styles.searchSection}
            disabled={section.disabled}
            hideArrow
            isActive={section.id === activeSection}
            isLoading={section.isLoading}
            isNotified={section.isNotified}
            label={section.label}
            onClick={() => onClickSection && onClickSection(section.id)}
            placeholder={section.placeholder}
            value={section.value}
          />
        )}
      </div>
    );
  };

  return (
    <div
      {...rest}
      className={classNames(styles.component, className, {
        [styles.hasFourSections]: sections.length === 4,
        [styles.hasThreeSections]: sections.length === 3,
        [styles.hasAreaSelectorLedge]: hasAreaSelectorLedge,
        [styles.hasAreaSelectorSection]: hasAreaSelectorSection,
        [styles.isActive]: activeSection !== -1,
        [styles.isCompact]: compact,
      })}
    >
      {hasAreaSelectorLedge && (
        <SearchBarAreaSelectorLedge
          areas={(areaSection?.dropdownData as AreaData)?.areas ?? []}
          className={styles.areaSelectorLedge}
        />
      )}

      <div className={styles.searchBarWrapper}>
        {/* Should be shown only for mobile */}
        {(hasAreaSelectorSection || hasAreaSelectorLedge) && (
          <div
            className={classNames(
              styles.searchSectionWrapper,
              styles.areaSelectorMobileWrapper,
            )}
          >
            <SearchBarAreaSelectorLedge
              areas={(areaSection?.dropdownData as AreaData)?.areas ?? []}
              hideConnectionCurve
            />
          </div>
        )}

        {sections?.map(renderSection)}

        <Button
          className={styles.button}
          disabled={!canSubmit}
          onClick={onClickSearch}
          text={buttonLabel}
        />
      </div>

      {!buttonMode && (
        <SearchBarDropdown
          activeSection={activeSection}
          customRangeStyle={customRangeStyle}
          isLoadingDate={isLoadingDate}
          isLoadingLocations={isLoadingLocations}
          onChangeDate={onChangeDate}
          onChangeDuration={onChangeDuration}
          onChangeLocations={onChangeLocations}
          onChangePersons={onChangePersons}
          onChangeTransportation={onChangeTransportation}
          onClickBack={onClickBack}
          onClickClose={onClickClose}
          onClickNext={onClickNext}
          onClickSearch={onClickSearch}
          searchSettings={searchSettings}
          sections={sections}
        />
      )}
    </div>
  );
};

SearchBarWrapper.displayName = 'SearchBarWrapper';

export default SearchBarWrapper;
